import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LogIn, Loader2 } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

export function AdminLoginForm({ heading, subtitle }) {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  }

  return (
    <form
      onSubmit={onSubmit}
      autoComplete="on"
      className="space-y-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm"
    >
      {heading ? (
        <div className="mb-2">
          <div className="text-xs uppercase tracking-[0.2em] text-oj-brand font-semibold">Admin</div>
          <h1 className="mt-2 text-3xl font-display font-black tracking-tight text-oj-ink">{heading}</h1>
          {subtitle ? <p className="mt-2 text-sm text-zinc-500">{subtitle}</p> : null}
        </div>
      ) : null}
      <label className="block" htmlFor="admin-email">
        <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Email</span>
        <input
          id="admin-email"
          name="email"
          type="email"
          required
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="support@ojavana.com"
          data-testid="admin-login-email"
          className="mt-1.5 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:border-oj-brand"
        />
      </label>
      <label className="block" htmlFor="admin-password">
        <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Password</span>
        <input
          id="admin-password"
          name="password"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-login-password"
          className="mt-1.5 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:border-oj-brand"
        />
      </label>
      {error && (
        <div data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={loading}
        data-testid="admin-login-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-oj-ink text-white font-semibold px-4 py-3 rounded-md hover:bg-zinc-800 transition disabled:opacity-60"
      >
        {loading ? <Loader2 className="size-4 animate-spin" /> : <LogIn className="size-4" />}
        {loading ? "Signing in..." : "Sign in"}
      </button>
    </form>
  );
}

export default function AdminLogin() {
  const { admin } = useAdminAuth();
  const location = useLocation();

  if (admin && admin !== false) {
    const to = location.state?.from || "/admin";
    return <Navigate to={to} replace />;
  }

  if (admin === null) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="text-sm text-zinc-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-login-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <AdminLoginForm
          heading="Sign in"
          subtitle="Same login for the inbox, CMS pages, Offers, vouchers, and Mailco settings. The browser can save this email and password."
        />
      </div>
    </div>
  );
}
